import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import LandingPage from './components/landing/LandingPage';
import LoginPage from './components/LoginPage';
import AboutPage from './components/AboutPage';
import WebsiteGeneratorPage from './components/WebsiteGeneratorPage';
import ProfilePage from './components/ProfilePage';
import KnowledgeBasePage from './components/KnowledgeBasePage';
import EnterprisePage from './components/EnterprisePage';
import EnterpriseBindPage from './components/EnterpriseBindPage';
import PricingPage from './components/pricing/PricingPage';
import OrdersPage from './components/OrdersPage';
import EducationAI from './components/education-ai';
import AdminPage from './components/admin/AdminPage';
import WebsiteEditorPage from './components/website-editor/WebsiteEditorPage';
import PublicWebsitePage from './components/public-website/PublicWebsitePage';
import WebsiteAnalyticsPage from './components/website-analytics/WebsiteAnalyticsPage';
import MyWebsitePage from './components/MyWebsitePage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { I18nContext, useI18nProvider } from './i18n';
import './styles/globals.css';

function PageTransition({ children }) {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [phase, setPhase] = useState('visible'); // 'visible' | 'fade-out' | 'fade-in'
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;

    // Step 1: fade out current page
    setPhase('fade-out');

    const fadeOutTimer = setTimeout(() => {
      // Step 2: swap content while invisible
      setDisplayChildren(children);
      prevPathRef.current = location.pathname;

      // Step 3: fade in new page
      requestAnimationFrame(() => {
        setPhase('fade-in');
      });
    }, 300);

    return () => clearTimeout(fadeOutTimer);
  }, [location.pathname, children]);

  // After fade-in completes, mark as fully visible
  useEffect(() => {
    if (phase === 'fade-in') {
      const timer = setTimeout(() => setPhase('visible'), 350);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  const getStyle = () => {
    switch (phase) {
      case 'fade-out':
        return {
          opacity: 0,
          transform: 'translateY(-8px) scale(0.995)',
          transition: 'opacity 280ms ease-in, transform 280ms ease-in',
        };
      case 'fade-in':
        return {
          opacity: 1,
          transform: 'translateY(0) scale(1)',
          transition: 'opacity 320ms ease-out, transform 320ms ease-out',
        };
      default:
        return {
          opacity: 1,
          transform: 'translateY(0) scale(1)',
          transition: 'none',
        };
    }
  };

  return <div style={getStyle()}>{displayChildren}</div>;
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <PageTransition>
      <Routes location={location}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/generate" element={<WebsiteGeneratorPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
        <Route path="/my-website" element={<ProtectedRoute><MyWebsitePage /></ProtectedRoute>} />
        <Route path="/website/:websiteId/edit" element={<ProtectedRoute><WebsiteEditorPage /></ProtectedRoute>} />
        <Route path="/website/:websiteId/analytics" element={<ProtectedRoute><WebsiteAnalyticsPage /></ProtectedRoute>} />
        <Route path="/e/:shortcode" element={<PublicWebsitePage />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace"
          element={
            <ProtectedRoute>
              <EducationAI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge"
          element={
            <ProtectedRoute>
              <KnowledgeBasePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/enterprise"
          element={
            <ProtectedRoute>
              <EnterprisePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/enterprise-bind"
          element={
            <ProtectedRoute>
              <EnterpriseBindPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
      </Routes>
    </PageTransition>
  );
}

function App() {
  const i18n = useI18nProvider();

  return (
    <I18nContext.Provider value={i18n}>
      <Router>
        <AnimatedRoutes />
      </Router>
    </I18nContext.Provider>
  );
}

export default App;
